import React, { useEffect, useMemo, useState } from "react";
import {
    Checkbox, Icon, SelectPicker, CheckPicker,
    Input, Whisper, Tooltip
} from "rsuite";
import styled, { css } from "styled-components";

import { renderColumn, capitalizeString } from "utils";
import CheckboxFullControl from "components/Form/CheckboxFullControl";
import TableEditCellsNew from "components/Table/TableEditCells/TableEditCellsNew";


export default ({
    dataList = [],
    dialerList = [],
    mccMncList = [],
    supplierList = [],
    exceptionList = [],


    loading,
    reportLoading,
    service,

    editKeys,
    activeRoutes,
    noAutoDeleteRoutes,
    lockToTrustedRoutes,

    formModel,
    modifyFormModel,
    createFormDefaultData,

    extraOnChange,

    setEditKeys,
    setActiveRoutes,
    setNoAutoDeleteRoutes,
    setLockToTrustedRoutes,
    ...props
}) => {

    const defaultCreateExtraData = useMemo(() => ({
        active: true,
        permanent: false,
        lock_to_reliable: false
    }), []);

    const [createExtraData, setCreateExtraData] = useState(defaultCreateExtraData);

    const modifyExtraData = useMemo(() => {
        const next = {};

        activeRoutes.forEach((active, routingKey) => {
            next[routingKey] = {
                ...next[routingKey],
                active
            };
        });

        noAutoDeleteRoutes.forEach((permanent, routingKey) => {
            next[routingKey] = {
                ...next[routingKey],
                permanent
            };
        });

        lockToTrustedRoutes.forEach((lockToReliable, routingKey) => {
            next[routingKey] = {
                ...next[routingKey],
                lock_to_reliable: lockToReliable
            };
        });

        return next;
    }, [activeRoutes, noAutoDeleteRoutes, lockToTrustedRoutes]);

    useEffect(() => {
        const editableKeySet = new Set(editKeys);

        setActiveRoutes(prev => {
            const next = new Map(prev);
            next.forEach((_, routingKey) => {
                if (!editableKeySet.has(routingKey)) {
                    next.delete(routingKey);
                }
            });
            return next.size === prev.size ? prev : next;
        });

        setNoAutoDeleteRoutes(prev => {
            const next = new Map(prev);
            next.forEach((_, routingKey) => {
                if (!editableKeySet.has(routingKey)) {
                    next.delete(routingKey);
                }
            });
            return next.size === prev.size ? prev : next;
        });

        setLockToTrustedRoutes(prev => {
            const next = new Map(prev);
            next.forEach((_, routingKey) => {
                if (!editableKeySet.has(routingKey)) {
                    next.delete(routingKey);
                }
            });
            return next.size === prev.size ? prev : next;
        });
    }, [editKeys, setActiveRoutes, setNoAutoDeleteRoutes, setLockToTrustedRoutes]);

    const columns = useMemo(() => [
        {
            id: "type",
            name: "Type",
            dataKey: "type",
            dataType: "string",
            align: "left",
            className: "type",
            // width: 120,
            width: 120,
            value: ({ type }) => {
                return type ? capitalizeString(type) : "";
            },
            showOnlyOnEdit: true,
            searchable: false,
            accepter: SelectPicker,
            placeholder: "Type",
            data: exceptionList || [],
            rowModel: modifyFormModel,
            valueKey: "value",
            labelKey: "name",
            cleanable: false,
            onChange: (type, rowData) => {
                if (type === "locked") {
                    return;
                }

                if (rowData?.routing_key == null) {
                    setCreateExtraData(prev => ({ ...prev, lock_to_reliable: false }));
                    return;
                }

                setLockToTrustedRoutes(prev => {
                    const next = new Map(prev);
                    next.set(rowData.routing_key, false);
                    return next;
                });
            }
        },
        {
            id: "supplier_id",
            name: "Supplier",
            dataKey: "supplier_id",
            dataType: "nullable",
            value: ({ supplier_name }) => {
                return supplier_name || "";
            },
            defaultValue: ({ supplier_id }) => {
                return supplier_id;
            },
            align: "left",
            className: "supplier_id",
            placeholder: "Supplier...",
            // width: 260,
            flexGrow: 1,
            minWidth: 180,
            maxWidth: 240,

            virtualized: true,
            style: {
                fontSize: 12,
                height: 36
            },
            menuStyle: { width: 360 },
            loading: loading || reportLoading,
            // disabled: reportLoading,
            // cleanable: !isLockedException,
            accepter: SelectPicker,
            data: supplierList || [],
            rowModel: modifyFormModel,
            valueKey: "supplier_id",
            labelKey: "name"
        },
        {
            id: "supplier_trunk_id",
            name: "Trunk",
            dataKey: "supplier_trunk_id",
            dataType: "nullable",
            value: ({ supplier_trunk_name }) => {
                return supplier_trunk_name || "";
            },
            defaultValue: ({ supplier_trunk_id }) => {
                return supplier_trunk_id;
            },
            align: "left",
            className: "supplier_trunk_id",
            placeholder: "Trunk...",
            // width: 260,
            // flexGrow: 1,
            width: 160,
            menuStyle: { width: 240 },
            maxWidth: 200,
            loading: loading,
            disabledOnMissingParam: { "supplier_trunk_id": "supplier_id" },
            cleanable: true,
            searchable: false,
            accepter: SelectPicker,
            data: (value) => {
                const supplier = supplierList.find(supplier => supplier.supplier_id === value.supplier_id);
                return supplier?.trunk_list ? supplier.trunk_list : [];
            },
            rowModel: modifyFormModel,
            valueKey: "id",
            labelKey: "name"
        },
        {
            id: "senderid",
            name: "Sender ID",
            dataKey: "senderid",
            dataType: "string",
            align: "left",
            placeholder: "Sender ID...",
            className: "senderid",
            // width: 120,
            width: 120,
            // maxWidth: 220,

            accepter: Input
        },
        {
            id: "mcc_mnc",
            name: "Destination",
            dataKey: "mcc_mnc",
            dataType: "nullable",
            value: ({ mcc_mnc }) => {
                const mccMnc = mccMncList.find(item => item.mcc_mnc === mcc_mnc);

                return mccMnc ? mccMnc.nameMccMnc : "";
            },
            align: "left",
            placeholder: "Destination...",

            // minWidth: 300,
            flexGrow: 1,
            minWidth: 160,
            maxWidth: 180,

            style: {
                fontSize: 12,
                height: 36
            },
            menuStyle: {
                width: 360
            },

            accepter: SelectPicker,
            data: mccMncList,
            valueKey: "mcc_mnc",
            labelKey: "nameMccMnc"
        },
        {
            id: "dialer_id_list",
            name: "Dialer",
            dataKey: "dialer_id_list",
            dataType: "array",
            className: "dialer_id_list",
            value: ({ dialer_list }) => {
                const currentDialerNameList = dialer_list ? dialer_list.map((item) => item.dialer_name) : [];

                const dialersList = currentDialerNameList.join(", "); // Повний список для tooltip

                let dialersText;
                if (currentDialerNameList.length === 1) {
                    dialersText = currentDialerNameList[0];
                } else if (currentDialerNameList.length > 1) {
                    const firstName = currentDialerNameList[0];
                    const secondName = currentDialerNameList[1];

                    const maxChars = Math.floor(260 / 8);
                    const ellipsisLength = 3; // "..."
                    const separatorLength = 2; // ", "

                    const availableForSecond = maxChars - firstName.length - separatorLength - ellipsisLength;

                    if (availableForSecond > 5) { // Мінімум 5 символів для другого імені
                        const truncatedSecond = secondName.length > availableForSecond
                            ? secondName.substring(0, availableForSecond)
                            : secondName;
                        dialersText = `${firstName}, ${truncatedSecond}...`;
                    } else {
                        // Якщо навіть для 5 символів немає місця
                        dialersText = `${firstName}...`;
                    }
                } else {
                    dialersText = "";
                }

                return (
                    <Whisper
                        disabled={currentDialerNameList.length <= 1}
                        speaker={<Tooltip>{dialersList}</Tooltip>}
                        trigger="hover"
                        placement="auto"
                    >
                        <span style={{
                            display: "block",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "100%"
                        }}>
                            {dialersText}
                        </span>
                    </Whisper>
                );
            },
            defaultValue: ({ dialer_id_list }) => {
                return dialer_id_list || [];
            },
            disabledFunc: ({ routing_key, lock_to_reliable }) => {
                if (routing_key == null) {
                    return Boolean(createExtraData.lock_to_reliable);
                }

                return lockToTrustedRoutes.get(routing_key)
                    ?? Boolean(lock_to_reliable);
            },

            align: "left",
            flexGrow: 1,
            // minWidth: 220,
            minWidth: 180,
            maxWidth: 240,
            style: {
                fontSize: 12,
                height: 36
            },
            menuStyle: {
                width: 360
            },
            placeholder: "Dialers...",

            loading: loading || reportLoading,
            // disabled: reportLoading,

            accepter: CheckPicker,
            data: dialerList || [],
            rowModel: modifyFormModel,
            valueKey: "id",
            labelKey: "name"
        },
        {
            id: "comment",
            name: "Comment",
            dataKey: "comment",
            dataType: "string",
            value: ({ comment }) => {
                return (
                    comment ? <Whisper
                        speaker={<Tooltip>{comment}</Tooltip>}
                        trigger="hover"
                        placement="bottom"
                    >
                        <SpanTextNoWrap className="tableFilters__previewText">
                            <Icon icon="comment" />
                        </SpanTextNoWrap>
                    </Whisper> : comment
                );
            },
            align: "center",
            flexGrow: 1,
            minWidth: 150,
            placeholder: "Comment...",
            accepter: Input
        }, // lock_to_reliable
        {
            id: "lock_to_reliable",
            name: "Lock to trusted",
            dataKey: "lock_to_reliable",
            dataType: "boolean",
            customField: true,
            defaultValue: ({ lock_to_reliable }) => {
                return lock_to_reliable;
            },
            onChange: (checked) => {
                setCreateExtraData(prev => ({ ...prev, lock_to_reliable: checked }));
            },
            value: ({ routing_key, type, lock_to_reliable }, currentFormValue) => {
                const notEditing = !editKeys.find(key => key === routing_key);
                const currentType = currentFormValue?.type ?? type;
                const isNotTypeLocked = currentType !== "locked";
                const currentLockToTrustedValue = lockToTrustedRoutes.has(routing_key)
                    ? lockToTrustedRoutes.get(routing_key)
                    : lock_to_reliable;

                return (
                    <StyledCheckbox
                        styledIntend={notEditing}
                        disabled={notEditing || isNotTypeLocked}
                        checked={currentLockToTrustedValue}
                        onChange={(_, checked) => {
                            setLockToTrustedRoutes(prev => {
                                const next = new Map(prev);
                                next.set(routing_key, checked);
                                return next;
                            });
                        }}
                    />
                )
            },
            disabledFunc: ({ type }) => {
                return type !== "locked";
            },
            label: "Lock to trusted",
            showLabelOnCreate: true,
            labelStyle: {
                checkboxLabel: true,
                compactCheckboxLabel: true,
                noMargin: true
            },
            extraCreateProps: {
                checked: createExtraData.lock_to_reliable,
                style: {
                    justifyContent: "flex-end"
                }
            },
            align: "center",
            width: 105,
            createWidth: 150,

            accepter: CustomCheckbox
        },
        {
            id: "permanent",
            name: "No auto delete",
            dataKey: "permanent",
            dataType: "boolean",
            customField: true,
            defaultValue: ({ permanent }) => {
                return permanent;
            },
            onChange: (checked) => {
                setCreateExtraData(prev => ({ ...prev, permanent: checked }));
            },
            value: ({ routing_key, permanent }) => {
                const notEditing = !editKeys.find(key => key === routing_key);
                const currentNoAutoDeleteValue = noAutoDeleteRoutes.has(routing_key)
                    ? noAutoDeleteRoutes.get(routing_key)
                    : permanent;

                return (
                    <StyledCheckbox
                        styledIntend={notEditing}
                        disabled={notEditing}
                        checked={currentNoAutoDeleteValue}
                        onChange={(_, checked) => {
                            setNoAutoDeleteRoutes(prev => {
                                const next = new Map(prev);
                                next.set(routing_key, checked);
                                return next;
                            });
                        }}
                    />
                )
            },
            label: "No auto delete",
            showLabelOnCreate: true,
            labelStyle: {
                checkboxLabel: true,
                compactCheckboxLabel: true,
                noMargin: true
            },
            extraCreateProps: {
                checked: createExtraData.permanent,
                style: {
                    justifyContent: "flex-start"
                }
            },
            align: "center",
            width: 100,
            createWidth: 148,

            accepter: CustomCheckbox
        },
        {
            id: "active",
            name: "Active",
            dataKey: "active",
            dataType: "boolean",
            customField: true,
            defaultValue: ({ active }) => {
                return active;
            },
            onChange: (checked) => {
                setCreateExtraData(prev => ({ ...prev, active: checked }));
            },
            value: ({ routing_key, active }) => {
                const notEditing = !editKeys.find(key => key === routing_key);
                return (
                    <StyledCheckbox
                        styledIntend={notEditing}
                        disabled={notEditing}
                        checked={Boolean(activeRoutes.get(routing_key) ?? active)}
                        onChange={(_, checked) => {
                            setActiveRoutes(prev => {
                                const next = new Map(prev);
                                next.set(routing_key, checked);
                                return next;
                            });
                        }}
                    />
                )
            },
            label: "Active",
            showLabelOnCreate: true,
            labelStyle: {
                checkboxLabel: true
            },
            extraCreateProps: {
                checked: createExtraData.active
            },
            align: "center",
            width: 70,

            accepter: CustomCheckbox
        },
        {
            id: "modified",
            name: "Modified",
            dataKey: "modified",
            value: (rowData) => {
                const previewParts = {
                    modified_date: rowData["modified_at"],
                    created_date: rowData["created_at"],
                    name: rowData["account_user_name"],
                    role: rowData["account_user_role"]
                };

                const date = previewParts.modified_date || previewParts.created_date;
                const userName = previewParts.name ? `${previewParts.name} (${previewParts.role})` : null;

                const dateObject = new Date(date);
                const options = {
                    dateStyle: "medium", timeStyle: "medium"
                };
                const dateString = dateObject.toLocaleString("en-US", options);

                const userNameText = `Modified by ${userName}`;

                const whisperMessage = (
                    userName ? <>
                        {userNameText} <br /> at {dateString}
                    </> : <>
                        {dateString}
                    </>
                );

                return (<>
                    <Whisper
                        speaker={<Tooltip>{whisperMessage}</Tooltip>}
                        trigger="hover"
                        placement="auto"
                    >
                        <SpanTextNoWrap className="tableFilters__previewText">
                            <Icon icon="user" />
                        </SpanTextNoWrap>
                    </Whisper>
                </>)
            },
            align: "center",
            width: 75
        }

    ], [dataList, editKeys, activeRoutes, noAutoDeleteRoutes, lockToTrustedRoutes, mccMncList, exceptionList, supplierList, dialerList, createExtraData]);

    const renderedColumns = columns.map(renderColumn);

    return (
        <>
            <StyledTable
                rowKey="routing_key"
                rowIdKey="routing_key"
                autoHeight={false}
                shouldUpdateScroll={false}
                virtualized
                headerHeight={46}
                rowHeight={44}
                height={662}

                rowClassName={(rowData) => {
                    var typeClass = "blocked";
                    var statusClass = rowData && rowData.active ? "" : "deactivated";


                    if (rowData && rowData.type === "allowed") {
                        typeClass = "allowed";
                    }

                    if (rowData && rowData.type === "locked") {
                        typeClass = "locked";
                    }

                    return `${typeClass} ${statusClass}`;
                }}

                formModel={formModel}
                data={dataList}
                extraDisabledModify={reportLoading}
                extraDisabledCreate={reportLoading}

                errorPlacement={"bottom"}
                columns={columns}
                addButtonWidth={120}
                setEditKeys={setEditKeys}
                extraOnChange={extraOnChange}

                createFormDefaultValue={createFormDefaultData}
                createExtraData={createExtraData}
                extraData={modifyExtraData}
                extraOnSubmit={() => {
                    setCreateExtraData(defaultCreateExtraData);
                }}
                {...props}
            >
                {renderedColumns}
            </StyledTable>

        </>
    )
}

const StyledTable = styled(TableEditCellsNew)`
    && {
        .tableFilters__previewText {
            display: block;
            line-height: 20px;
            margin-top: 7px;
            word-break: normal;
        }
        
        .tableFilters_buttons {
            align-items: center;
            display: flex;
            height: 20px;
            margin-top: 7px;
        }

        .tableFilters_buttons .rs-btn-sm {
            min-width: 28px;
            min-height: 28px;
            padding: 0px;
            margin-right: 8px;
        }

        .rs-table-row.deactivated .rs-table-cell {
            color: #999999;
        }

        .rs-table-row.blocked .rs-table-cell {
            background: #f7dddd;
        }
        
        .rs-table-row.allowed .rs-table-cell {
            background: #e2f6de;
        }

        .rs-table-row.locked .rs-table-cell {
            background: #f7f4d7;
        }

        .rs-form-control-wrapper .rs-error-message.rs-error-message-show {
            top: -6px;
            right: 0px;
            height: 18px;
            border-radius: 10px;
            padding-top: 0;
            border: 0px;

            .rs-error-message-arrow {
                display: none;
            }

            .rs-error-message-inner {
                font-size: 11px;
            }
        }
    }
`;


const CustomCheckbox = styled(CheckboxFullControl)`
    && {
        display: flex;
        flex-direction: row;
        justify-content: center;
    }
`;

const StyledCheckbox = styled(Checkbox)`
    && {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    ${props => props.styledIntend && css`
        top: -7px;
    `}

    && .rs-checkbox-wrapper {
        // left: 24px;
    }
`;

const SpanTextNoWrap = styled.span`
    && {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;
